<script lang="ts" setup>
	import useVuelidate from '@vuelidate/core'
	import {
		alpha,
		helpers,
		integer,
		maxLength,
		required,
	} from '@vuelidate/validators'
	import { computed, reactive, ref } from 'vue'
	import { watch } from 'vue'
	import { useToast } from 'vue-toastification'

	import LoadingModal from '@/components/LoadingModal.vue'
	import ModalComponent from '@/components/ModalComponent.vue'
	import ShadowBox from '@/components/ShadowBox.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledDateInput from '@/components/StyledDateInput.vue'
	import StyledInput from '@/components/StyledInput.vue'
	import StyledTextarea from '@/components/StyledTextarea.vue'

	import { HotelRate } from '@/interfaces/hotel'

	import useRate from '@/internal/composables/useRate'
	import useRateComments from '@/internal/composables/useRateCommets'
	import useHotelForm from '@/internal/modules/providers/composables/useHotelForm'
	import { Hotel } from '@/internal/modules/providers/interfaces/Hotel'

	import PaginateNav from '@/modules/trips/components/PaginateNav.vue'

	import RateEntry from '../components/RateEntry.vue'
	import useHotelRates from '../composables/useHotelRate'

	interface Props {
		hotel: Hotel
	}

	const props = defineProps<Props>()
	const openModal = ref(false)
	const toast = useToast()
	const createHotelRateLoading = ref(false)
	const openModalEdit = ref(false)
	const commentary = ref('')
	const openModalDelete = ref(false)
	const selectedRate = ref<HotelRate | null>(null)

	const rules = computed(() => ({
		amount: {
			required: helpers.withMessage(
				'El monto de la tarifa es requerido',
				required
			),
		},
		currency: {
			minLength: helpers.withMessage(
				'La moneda de la tarifa debe tener un mínimo de 3 caracteres',
				maxLength(3)
			),
			alphanumeric: helpers.withMessage(
				'La moneda de la tarifa debe ser alfanumérica',
				alpha
			),
			maxLength: helpers.withMessage(
				'La moneda de la tarifa debe tener un máximo de 3 caracteres',
				maxLength(3)
			),
		},
		currencyConversion: {
			required: helpers.withMessage(
				'La conversión de moneda es requerida',
				required
			),
		},
		selectedCategory: {
			required: helpers.withMessage(
				'La categoría de habitación es requerida',
				required
			),
		},
		selectedBedAmount: {
			required: helpers.withMessage(
				'La cantidad de camas es requerida',
				required
			),
		},
		selectedBedType: {
			required: helpers.withMessage('El tipo de cama es requerido', required),
		},
	}))

	const {
		hotelRates,
		isLoading: isLoadingRates,
		page,
		refetch,
		isRefetching,
		createHotelRateAsync,
	} = useHotelRates({
		immediate: true,
		params: { id: props.hotel.id },
		config: { errorHandling: false },
	})

	const { updateRateAsync, deleteRateAsync, reactiveRateAsync } = useRate({
		immediate: false,
		params: { id: 0 },
	})

	const {
		form: hotelForm,
		handleUpdate,
		updateHotelLoading,
	} = useHotelForm(props.hotel)

	const toggleModal = () => {
		openModal.value = !openModal.value
	}

	const handlePage = (newPage: number | string) => {
		const pageNumber = Number(newPage)
		if (pageNumber >= 1 && pageNumber <= hotelRates.value?.meta.lastPage!) {
			page.value = newPage
			refetch()
		}
	}

	// Definir las opciones disponibles (deben coincidir con backend y otros componentes)
	const roomCategories = {
		H: 'Accessible room',
		I: 'Budget room',
		B: 'Business room',
		G: 'Comfort room',
		D: 'Deluxe room',
		X: 'Duplex',
		E: 'Executive room',
		C: 'Concierge/Executive Suite',
		F: 'Family room',
		S: 'Junior Suite/Mini Suite',
		P: 'Penthouse',
		R: 'Residential apartment',
		M: 'Standard',
		L: 'Studio',
		A: 'Superior',
		V: 'Villa',
	}

	const bedTypes = {
		D: 'Double',
		K: 'King size',
		P: 'Pull-out',
		Q: 'Queen-size',
		S: 'Single',
		T: 'TwinT',
		W: 'Water bed',
		'*': 'Bed type can vary',
	}

	// Generar el type_code dinámicamente
	const typeCode = computed(() => {
		if (
			form.selectedCategory &&
			form.selectedBedAmount &&
			form.selectedBedType
		) {
			return `${form.selectedCategory}${form.selectedBedAmount}${form.selectedBedType}`
		}
		return ''
	})

	interface Form {
		id?: number
		amount: string
		agreement: boolean
		typeCode: string
		selectedCategory: keyof typeof roomCategories | null
		selectedBedAmount: string | null
		selectedBedType: keyof typeof bedTypes | null
		currency: string
		currencyConversion: string
		vigency: string[]
	}

	const form = reactive<Form>({
		amount: '',
		agreement: false,
		typeCode: '',
		selectedCategory: null,
		selectedBedAmount: null,
		selectedBedType: null,
		currency: '',
		currencyConversion: '',
		vigency: [],
	})

	const v$ = useVuelidate(rules, form)

	const validateErrors = () => {
		v$.value.$touch()

		for (const field of v$.value.$silentErrors) {
			if (!Array.isArray(field.$message)) {
				toast.error(field.$message)
				return false
			}
		}

		return true
	}

	const toggleModalEdit = (rate: HotelRate) => {
		openModalEdit.value = !openModalEdit.value

		const formtedStartDate = new Date(rate.startDate)
			.toISOString()
			.split('T')[0]
		const formtedExpirationDate = new Date(rate.expirationDate)
			.toISOString()
			.split('T')[0]

		form.id = rate.id
		form.amount = rate.amount.toString()
		form.agreement = Boolean(rate.agreement)
		form.typeCode = rate.typeCode
		form.selectedCategory = rate.typeCode[0] as keyof typeof roomCategories
		form.selectedBedAmount = rate.typeCode[1] as string
		form.selectedBedType = rate.typeCode[2] as keyof typeof bedTypes
		form.currency = rate.currency
		form.currencyConversion = rate.currencyConversion
		form.vigency = [formtedStartDate, formtedExpirationDate]
	}

	const handleCreate = async () => {
		form.currencyConversion =
			form.currency !== 'MXN' ? form.currencyConversion : '1'

		if (!validateErrors()) {
			return
		}

		const data = await createHotelRateAsync({
			hotelId: props.hotel.id,
			amount: form.amount,
			agreement: form.agreement,
			typeCode: typeCode.value,
			currency: form.currency,
			currencyConversion: form.currencyConversion,
			startDate: form.vigency[0],
			expirationDate: form.vigency[1],
		})

		if (data) {
			await refetch()
			toggleModal()

			handleReset()
		}
	}

	const handleEdit = async () => {
		form.currencyConversion =
			form.currency !== 'MXN' ? form.currencyConversion : '1'

		if (!validateErrors()) {
			return
		}

		const data = await updateRateAsync({
			rateId: form.id!,
			amount: form.amount,
			agreement: form.agreement,
			typeCode: typeCode.value,
			currency: form.currency,
			currencyConversion: form.currencyConversion,
			startDate: form.vigency[0],
			expirationDate: form.vigency[1],
		})

		if (data) {
			await refetch()
			openModalEdit.value = false

			handleReset()
		}
	}

	const handleDelete = async () => {
		if (!commentary.value) {
			return toast.error(
				'Por favor, proporciona un comentario para la desactivación.'
			)
		}

		await deleteRateAsync({
			commentary: `Desactivación de tarifa: ${commentary.value}`,
			id: selectedRate.value?.id!,
		})
		await refetch()

		commentary.value = ''
		openModalDelete.value = false
	}

	const handleRestore = async () => {
		if (!commentary.value) {
			return toast.error(
				'Por favor, proporciona un comentario para la restauración.'
			)
		}

		await reactiveRateAsync({
			id: selectedRate.value?.id!,
			commentary: `Restauración de tarifa: ${commentary.value}`,
		})
		await refetch()

		commentary.value = ''
		openModalDelete.value = false
	}

	const activeModal = ref(false)

	const {
		data: comments,
		refetch: refetchComments,
		createCommentMutationAsync,
		rateId,
		isLoading: isLoadingComments,
		isFetching: isFetchingComments,
		isRefetching: isRefetchingComments,
	} = useRateComments({
		immediate: false,
		params: { id: '' },
	})

	const toggleActiveModal = (rate: HotelRate) => {
		selectedRate.value = rate
		activeModal.value = !activeModal.value
	}

	const handleComment = async () => {
		if (!commentary.value) {
			return toast.error(
				'Por favor, proporciona un comentario para la restauración.'
			)
		}

		await createCommentMutationAsync({
			id: selectedRate.value?.id.toString()!,
			commentary: commentary.value,
		})
		await refetchComments()

		commentary.value = ''
		openModalDelete.value = false
	}

	const selectRate = (rate: HotelRate) => {
		selectedRate.value = rate
	}

	const actionModalDelete = () => {
		openModalDelete.value = !openModalDelete.value
	}

	const handleReset = () => {
		form.amount = ''
		form.agreement = false
		form.typeCode = ''
		form.selectedCategory = null
		form.selectedBedAmount = null
		form.selectedBedType = null
		form.currency = ''
		form.currencyConversion = ''
		form.vigency = []
	}

	watch(selectedRate, async (rate) => {
		if (rate) {
			rateId.value = rate.id.toString()
			await refetchComments()
		}
	})
</script>

<template>
	<ShadowBox class="bg-white p-6 rounded-lg shadow-md">
		<!-- Sección para modificar ISH e IVA -->
		<div class="border-b pb-6 mb-8">
			<!-- Aumenta padding-bottom y margin-bottom -->
			<h1 class="text-2xl font-bold text-gray-800 mb-4">
				Configuración de Impuestos
			</h1>

			<form @submit.prevent="handleUpdate()">
				<div class="grid grid-cols-2 gap-4">
					<div>
						<label class="block text-gray-700 font-medium mb-1">ISH (%)</label>
						<input
							v-model="hotelForm.ish"
							type="text"
							placeholder="Ingrese el ISH"
							class="w-full border rounded-lg px-4 py-2 focus:ring focus:ring-blue-300"
							required
						/>
					</div>

					<div>
						<label class="block text-gray-700 font-medium mb-1">IVA (%)</label>
						<input
							v-model="hotelForm.vat"
							type="text"
							placeholder="Ingrese el IVA"
							class="w-full border rounded-lg px-4 py-2 focus:ring focus:ring-blue-300"
							required
						/>
					</div>
				</div>

				<!-- Botón separado con más aire -->
				<div class="flex justify-end mt-6">
					<!-- Aumenta separación -->
					<StyledButton
						class="bg-blue-600 hover:bg-blue-800 text-white px-6 py-2 rounded-lg transition font-medium"
						:loading="updateHotelLoading"
						:disabled="updateHotelLoading"
					>
						Guardar Impuestos
					</StyledButton>
				</div>
			</form>
		</div>

		<div class="flex justify-between items-center pb-4">
			<h1 class="text-2xl font-bold text-gray-800">Tarifas del Hotel</h1>

			<StyledButton
				class="bg-blue-600 hover:bg-blue-800 text-white px-4 py-2 rounded-md transition"
				@click="toggleModal"
			>
				+ Agregar Tarifa
			</StyledButton>
		</div>

		<!-- Listado de Tarifas -->
		<div
			v-if="hotelRates?.data?.length"
			class="mt-4 space-y-3"
		>
			<RateEntry
				v-for="rate in hotelRates?.data"
				:key="rate.id"
				:rate="rate"
				@action-modal="toggleModalEdit(rate)"
				@select-rate="selectRate(rate)"
				@action-modal-delete="actionModalDelete"
				@toggle-active-modal="toggleActiveModal(rate)"
			/>
		</div>

		<!-- Estado vacío -->
		<div
			v-else
			class="text-center text-gray-500 py-10"
		>
			<p>No hay tarifas registradas para este hotel.</p>
		</div>

		<!-- Paginador -->
		<div class="flex justify-end mt-4">
			<PaginateNav
				v-if="hotelRates?.data?.length"
				:page="page"
				:pagination="hotelRates"
				:data="hotelRates?.data || []"
				:handle-page="handlePage"
			/>
		</div>
	</ShadowBox>

	<!-- Modal de Creación de Tarifa -->
	<ModalComponent
		:model-value="openModal"
		@update:model-value="toggleModal"
	>
		<template #modalBody>
			<ShadowBox class="bg-white p-6 rounded-lg">
				<h2 class="text-xl font-semibold text-gray-800 mb-4">
					Agregar Nueva Tarifa
				</h2>

				<form
					@submit.prevent="handleCreate"
					class="flex flex-col gap-4"
				>
					<!-- Input para el monto y moneda -->
					<div class="grid grid-cols-4 gap-4">
						<label class="text-gray-700 col-span-3">
							Monto:
							<input
								v-model="form.amount"
								type="text"
								class="w-full border rounded px-3 py-2 mt-1"
							/>
						</label>

						<label class="text-gray-700">
							Moneda:
							<input
								v-model="form.currency"
								type="text"
								class="w-full border rounded px-3 py-2 mt-1"
								@input="
									(e) => {
										if (e.target) {
											form.currency = (
												e.target as HTMLInputElement
											).value.toUpperCase()
										}
									}
								"
							/>
						</label>
					</div>

					<!-- Input para la conversion -->
					<label
						class="text-gray-700"
						v-if="form.currency !== 'MXN'"
					>
						Conversión de moneda:
						<input
							v-model="form.currencyConversion"
							type="text"
							class="w-full border rounded px-3 py-2 mt-1"
						/>
					</label>

					<!-- Input de vigencia de tarifa -->
					<label class="text-gray-700">
						Vigencia de la tarifa:
						<StyledDateInput
							v-model="form.vigency"
							:range="true"
							class="w-full mt-1"
							placeholder="Fecha de inicio - Fecha de expiración"
							:min-date="new Date()"
						/>
					</label>

					<!-- Checkbox para agreement -->
					<label class="flex items-center space-x-2 text-gray-700">
						<input
							v-model="form.agreement"
							type="checkbox"
							class="w-5 h-5"
						/>
						<span>Es convenio</span>
					</label>

					<!-- Select para categoría de habitación -->
					<label class="text-gray-700">
						Categoría de habitación:
						<select
							v-model="form.selectedCategory"
							class="w-full border rounded px-3 py-2 mt-1"
						>
							<option
								v-for="(label, key) in roomCategories"
								:key="key"
								:value="key"
							>
								{{ label }}
							</option>
						</select>
					</label>

					<!-- Select para cantidad de camas -->
					<label class="text-gray-700">
						Cantidad de camas:
						<select
							v-model="form.selectedBedAmount"
							class="w-full border rounded px-3 py-2 mt-1"
						>
							<option
								v-for="n in 4"
								:key="n"
								:value="String(n)"
							>
								{{ n }}
							</option>
						</select>
					</label>

					<!-- Select para tipo de cama -->
					<label class="text-gray-700">
						Tipo de cama:
						<select
							v-model="form.selectedBedType"
							class="w-full border rounded px-3 py-2 mt-1"
						>
							<option
								v-for="(label, key) in bedTypes"
								:key="key"
								:value="key"
							>
								{{ label }}
							</option>
						</select>
					</label>

					<!-- Mostrar el código generado -->
					<div
						v-if="typeCode"
						class="text-gray-800 mt-2"
					>
						<strong>Código generado:</strong> {{ typeCode }}
					</div>

					<div class="w-full flex justify-end mt-4">
						<!-- Botón para guardar -->
						<StyledButton
							class="bg-blue-600 hover:bg-blue-800 text-white px-4 py-2 rounded-md transition"
							:disabled="createHotelRateLoading"
							:loading="createHotelRateLoading"
						>
							Guardar Tarifa
						</StyledButton>
					</div>
				</form>
			</ShadowBox>
		</template>
	</ModalComponent>

	<!-- Modal de editar la tarifa -->
	<ModalComponent
		:model-value="openModalEdit"
		@update:model-value="openModalEdit = false"
	>
		<template #modalBody>
			<ShadowBox class="bg-white p-6 rounded-lg">
				<h2 class="text-xl font-semibold text-gray-800 mb-4">Editar Tarifa</h2>

				<form
					@submit.prevent="handleEdit"
					class="flex flex-col gap-4"
				>
					<!-- Input para el monto y moneda -->
					<div class="grid grid-cols-4 gap-4">
						<label class="text-gray-700 col-span-3">
							Monto:
							<input
								v-model="form.amount"
								type="text"
								class="w-full border rounded px-3 py-2 mt-1"
							/>
						</label>

						<label class="text-gray-700">
							Moneda:
							<input
								v-model="form.currency"
								type="text"
								class="w-full border rounded px-3 py-2 mt-1"
								@input="
									(e) => {
										if (e.target) {
											form.currency = (
												e.target as HTMLInputElement
											).value.toUpperCase()
										}
									}
								"
							/>
						</label>
					</div>

					<!-- Input para la conversion -->
					<label
						class="text-gray-700"
						v-if="form.currency !== 'MXN'"
					>
						Conversión de moneda:
						<input
							v-model="form.currencyConversion"
							type="text"
							class="w-full border rounded px-3 py-2 mt-1"
						/>
					</label>

					<!-- Input de vigencia de tarifa -->
					<label class="text-gray-700">
						Vigencia de la tarifa:
						<StyledDateInput
							v-model="form.vigency"
							:range="true"
							class="w-full mt-1"
							placeholder="Fecha de inicio - Fecha de expiración"
							:min-date="new Date()"
						/>
					</label>

					<!-- Checkbox para agreement -->
					<label class="flex items-center space-x-2 text-gray-700">
						<input
							v-model="form.agreement"
							type="checkbox"
							class="w-5 h-5"
						/>
						<span>Es convenio</span>
					</label>

					<!-- Select para categoría de habitación -->
					<label class="text-gray-700">
						Categoría de habitación:
						<select
							v-model="form.selectedCategory"
							class="w-full border rounded px-3 py-2 mt-1"
							required
						>
							<option
								v-for="(label, key) in roomCategories"
								:key="key"
								:value="key"
							>
								{{ label }}
							</option>
						</select>
					</label>

					<!-- Select para cantidad de camas -->
					<label class="text-gray-700">
						Cantidad de camas:
						<select
							v-model="form.selectedBedAmount"
							class="w-full border rounded px-3 py-2 mt-1"
							required
						>
							<option
								v-for="n in 4"
								:key="n"
								:value="String(n)"
							>
								{{ n }}
							</option>
						</select>
					</label>

					<!-- Select para tipo de cama -->
					<label class="text-gray-700">
						Tipo de cama:
						<select
							v-model="form.selectedBedType"
							class="w-full border rounded px-3 py-2 mt-1"
							required
						>
							<option
								v-for="(label, key) in bedTypes"
								:key="key"
								:value="key"
							>
								{{ label }}
							</option>
						</select>
					</label>

					<!-- Mostrar el código generado -->
					<div
						v-if="typeCode"
						class="text-gray-800 mt-2"
					>
						<strong>Código generado:</strong> {{ typeCode }}
					</div>

					<div class="w-full flex justify-end mt-4">
						<!-- Botón para guardar -->
						<StyledButton
							class="bg-blue-600 hover:bg-blue-800 text-white px-4 py-2 rounded-md transition"
							:disabled="createHotelRateLoading"
							:loading="createHotelRateLoading"
						>
							Guardar cambios
						</StyledButton>
					</div>
				</form>
			</ShadowBox>
		</template>
	</ModalComponent>

	<!-- Modal de confirmación para eliminar tarifa o habilitar -->
	<ModalComponent
		:model-value="openModalDelete"
		@update:model-value="
			() => {
				openModalDelete = false
				commentary = ''
			}
		"
	>
		<template #modalBody>
			<ShadowBox class="bg-white p-6 rounded-lg">
				<h2 class="text-xl font-semibold text-gray-800 mb-4">
					{{ selectedRate?.disabled ? 'Habilitar' : 'Eliminar' }} Tarifa
				</h2>
				<p class="text-gray-700 mb-4">
					{{
						selectedRate?.disabled ?
							'¿Deseas habilitar esta tarifa?'
						:	'¿Deseas eliminar esta tarifa?'
					}}
				</p>

				<StyledTextarea
					v-model="commentary"
					:placeholder="`${
						selectedRate?.disabled ?
							'Comentario para habilitar la tarifa'
						:	'Comentario para eliminar la tarifa'
					}`"
					class="w-full mt-1"
					rows="4"
				/>

				<div class="flex">
					<div
						class="w-full justify-end mt-4"
						v-if="!selectedRate?.disabled"
					>
						<StyledButton
							class="bg-red-600 hover:bg-red-800 text-white px-4 py-2 rounded-md transition"
							@click="handleDelete()"
							:disabled="!commentary"
						>
							Eliminar
						</StyledButton>
					</div>

					<div
						class="w-full justify-end mt-4"
						v-else
					>
						<StyledButton
							class="bg-green-600 hover:bg-green-800 text-white px-4 py-2 rounded-md transition"
							@click="handleRestore()"
							:disabled="!commentary"
						>
							Habilitar
						</StyledButton>
					</div>

					<!-- Botón para cancelar -->
					<div
						class="w-full flex justify-end mt-4"
						v-if="!selectedRate?.disabled"
					>
						<StyledButton
							class="bg-gray-600 hover:bg-gray-800 text-white px-4 py-2 rounded-md transition"
							@click="actionModalDelete"
						>
							Cancelar
						</StyledButton>
					</div>
				</div>
			</ShadowBox>
		</template>
	</ModalComponent>

	<!-- Modal de comentarios y logs -->
	<ModalComponent
		:model-value="activeModal"
		@update:model-value="activeModal = false"
	>
		<template #modalBody>
			<ShadowBox class="bg-white p-6 rounded-lg">
				<h2 class="text-xl font-semibold text-gray-800 mb-4">
					Comentarios de la Tarifa
				</h2>
				<!-- Espacio para nuevo comentario -->
				<div class="mt-4">
					<StyledTextarea
						v-model="commentary"
						placeholder="Escribe un comentario..."
						class="w-full mt-1"
						rows="4"
					/>

					<div class="flex justify-end mt-4">
						<StyledButton
							class="bg-blue-600 hover:bg-blue-800 text-white px-4 py-2 rounded-md transition"
							@click="handleComment()"
							:disabled="!commentary"
						>
							Agregar Comentario
						</StyledButton>
					</div>
				</div>

				<div v-if="isLoadingComments && !isRefetchingComments">
					<p class="text-gray-700">Cargando comentarios...</p>
				</div>

				<div v-else>
					<div
						v-for="comment in comments"
						:key="comment.id"
						class="border-b py-2"
					>
						<p class="text-gray-700">
							<strong>{{ comment?.creatorName }}</strong
							>:
							{{ comment.commentary }}
						</p>
						<p class="text-gray-500 text-sm">Fecha: {{ comment.createdAt }}</p>
					</div>
				</div>
			</ShadowBox>
		</template>
	</ModalComponent>

	<!-- Loader -->
	<LoadingModal :when="isLoadingRates || isRefetching || updateHotelLoading" />
</template>
